import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import debounce from "lodash/debounce";
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { Loader2, Sparkles } from "lucide-react";
import {
  type Automation,
  type AutomationCosts,
  automationQueries,
  createAutomation,
  updateAutomation,
  persistAutomationStatus,
  setGeneralConfig,
  type AutomationCreate,
} from "@/api/automations";
import { productQueries, type Product } from "@/api/product";
import { InitialSetup } from "@/components/automate/InitialSetup";
import { GeneralConfigCard } from "@/components/automate/GeneralConfigCard";
import AutomationWrapper from "@/components/automate/AutomationWrapper"; // <--- import your wrapper
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface ProductUIConfig {
  showFrequency: boolean;
  showActions: boolean;
  showVolume: boolean;
  actionLabel: string;
  volumeLabel: string;
}
const fallbackConfig: ProductUIConfig = {
  showFrequency: true,
  showActions: true,
  showVolume: true,
  actionLabel: "Actions per Day",
  volumeLabel: "Estimated Daily Volume",
};

interface AutomationConfigProps {
  selectedPersonaId: string;
  hasTwitterAuth: boolean;
}

export default function AutomationConfig({
  selectedPersonaId,
  hasTwitterAuth,
}: AutomationConfigProps) {
  const [isSubmittingSetup, setIsSubmittingSetup] = useState(false);

  const queryClient = useQueryClient();

  // 1) Query: General config
  const {
    data: generalConfig,
    isLoading: isLoadingConfig,
    refetch: refetchConfig,
  } = useQuery({
    ...automationQueries.generalConfig(selectedPersonaId),
  });

  // 2) Query: Automations
  const {
    data: agentAutomations = [],
    isLoading: isLoadingAutomations,
    refetch: refetchAutomations,
  } = useQuery({
    ...automationQueries.automations(selectedPersonaId),
  });

  // 3) Query: Products
  const { data: products = [], isLoading: isLoadingProducts } = useQuery({
    ...productQueries.products(),
  });

  // 4) Query: Costs
  const {
    data: costs,
    isLoading: isLoadingCosts,
    refetch: refetchCosts,
  } = useQuery({
    ...automationQueries.costs(selectedPersonaId),
    enabled: Boolean(selectedPersonaId),
    placeholderData: {
      daily_costs: {},
      total_daily_cost: 0,
      total_monthly_cost: 0,
      operation_hours: 0,
      max_posts_per_day: 0,
      auto_replenish_details: {
        enabled: false,
        threshold: 0,
        amount: 0,
        estimated_frequency: null,
      },
    },
  });

  // Once config is loaded, refetch automations/costs
  useEffect(() => {
    if (!isLoadingConfig && generalConfig) {
      refetchAutomations();
      refetchCosts();
    }
  }, [isLoadingConfig, generalConfig, refetchAutomations, refetchCosts]);

  // If no Twitter or no general config => disable controls
  // (But the wrapper is already disabling pointer events if no Twitter.)
  // You might also want to disable if !generalConfig, up to you:
  const disableAll = !generalConfig; // e.g. you can combine with !hasTwitterAuth if you want

  // Filter relevant automation products
  const automationProducts = useMemo(() => {
    return products.filter((p) => p.automation_product === true);
  }, [products]);

  // Merge docs + products
  const mergedAutomations = useMemo(() => {
    return automationProducts.map((product) => {
      const existingDoc = agentAutomations.find(
        (a) => a.product_sku === product.product_sku,
      );
      return { product, automation: existingDoc || null };
    });
  }, [automationProducts, agentAutomations]);

  // Sort by metadata.sort_order
  const sortedItems = useMemo(() => {
    return [...mergedAutomations].sort((a, b) => {
      const aOrder = a.product.metadata?.sort_order ?? 9999;
      const bOrder = b.product.metadata?.sort_order ?? 9999;
      return aOrder - bOrder;
    });
  }, [mergedAutomations]);

  // Active vs. Inactive
  const activeItems = useMemo(() => {
    return sortedItems.filter(
      (item) => item.automation && item.automation.enabled,
    );
  }, [sortedItems]);

  const catalogItems = useMemo(() => {
    return sortedItems.filter(
      (item) => !item.automation || !item.automation.enabled,
    );
  }, [sortedItems]);

  // UI config helpers
  function getUIConfig(product: Product): ProductUIConfig {
    if (!product.automation_config) {
      return fallbackConfig;
    }
    return {
      showFrequency: product.automation_config.showFrequency ?? true,
      showActions: product.automation_config.showActions ?? true,
      showVolume: product.automation_config.showVolume ?? true,
      actionLabel: product.automation_config.actionLabel || "Actions per Day",
      volumeLabel:
        product.automation_config.volumeLabel || "Estimated Daily Volume",
    };
  }
  function getFrequencyCount(a: Automation | null, p: Product) {
    return getUIConfig(p).showActions && a
      ? (a.metadata?.frequency_count ?? 1)
      : 1;
  }
  function getEstimatedVolume(a: Automation | null, p: Product) {
    return getUIConfig(p).showVolume && a
      ? (a.metadata?.estimated_daily_volume ?? 1)
      : 1;
  }

  // Debounced save
  const debouncedSave = useMemo(
    () =>
      debounce(async (automationId: string, updates: Partial<Automation>) => {
        try {
          await updateAutomation(selectedPersonaId, automationId, updates);
          queryClient.invalidateQueries(
            automationQueries.automations(selectedPersonaId),
          );
          queryClient.invalidateQueries(
            automationQueries.costs(selectedPersonaId),
          );
        } catch {
          toast({
            title: "Error",
            description: "Failed to save automation config",
            variant: "destructive",
          });
        }
      }, 300),
    [selectedPersonaId, queryClient],
  );

  const handleConfigUpdate = useCallback(
    (automationId: string, updates: Partial<Automation>) => {
      if (disableAll) return; // can't update if not allowed

      // Local update
      queryClient.setQueryData(
        ["automations", selectedPersonaId],
        (old: Automation[] | undefined) => {
          if (!old) return old;
          return old.map((item) => {
            if (item.automation_id !== automationId) return item;
            if (updates.metadata) {
              return {
                ...item,
                metadata: {
                  ...item.metadata,
                  ...updates.metadata,
                },
              };
            }
            return { ...item, ...updates };
          });
        },
      );
      // Debounced server
      debouncedSave(automationId, updates);
    },
    [selectedPersonaId, queryClient, debouncedSave, disableAll],
  );

  useEffect(() => {
    return () => {
      debouncedSave.cancel();
    };
  }, [debouncedSave]);

  // Toggle existing doc
  async function handleToggleAutomation(
    automationId: string,
    enabled: boolean,
  ) {
    if (disableAll) return;
    try {
      await persistAutomationStatus(selectedPersonaId, automationId, enabled);
      queryClient.invalidateQueries(
        automationQueries.automations(selectedPersonaId),
      );
      queryClient.invalidateQueries(automationQueries.costs(selectedPersonaId));

      toast({
        title: enabled ? "Enabled" : "Disabled",
        description: "Automation toggled successfully",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description:
          error?.response?.data?.detail || "Failed to toggle automation",
        variant: "destructive",
      });
    }
  }

  // Create + enable doc
  async function handleCreateAutomation(
    productSku: string,
    productName: string,
    productDescription: string,
  ) {
    if (disableAll) return;
    try {
      const payload = {
        automation_type: productSku,
        product_sku: productSku,
        name: productName,
        description: productDescription,
        metadata: { frequency: "daily", frequency_count: 1 },
        enabled: true,
      } satisfies AutomationCreate;
      await createAutomation(selectedPersonaId, payload);
      queryClient.invalidateQueries(
        automationQueries.automations(selectedPersonaId),
      );

      toast({
        title: "Automation Enabled",
        description: productName,
      });
    } catch {
      toast({
        title: "Error",
        description: "Failed to create automation doc",
        variant: "destructive",
      });
    }
  }

  // Handle initial setup
  async function handleInitialSetup(payload: {
    operation_window_start: number;
    operation_window_end: number;
    max_posts_per_day: number;
    auto_replenish_credits: boolean;
    auto_replenish_threshold: number;
    auto_replenish_amount: number;
    timezone: string;
  }) {
    if (!hasTwitterAuth) return; // can't submit if no Twitter
    try {
      setIsSubmittingSetup(true);
      await setGeneralConfig(selectedPersonaId, payload);
      await refetchConfig();
      toast({
        title: "Setup Complete",
        description: "Your automation settings have been initialized.",
      });
    } catch {
      toast({
        title: "Setup Failed",
        description: "An error occurred during initial setup.",
        variant: "destructive",
      });
    } finally {
      setIsSubmittingSetup(false);
    }
  }

  // Summaries
  const totalDailyCost = activeItems.reduce((sum, item) => {
    const docDailyCost =
      costs?.daily_costs[item.automation!.automation_id]?.daily_cost || 0;
    return sum + docDailyCost;
  }, 0);

  function renderAutomationRow(
    automation: Automation | null,
    product: Product,
  ) {
    return (
      <AutomationRow
        key={product.product_sku}
        product={product}
        automation={automation}
        costs={costs ?? undefined}
        handleConfigUpdate={handleConfigUpdate}
        handleToggleAutomation={handleToggleAutomation}
        handleCreateAutomation={handleCreateAutomation}
        getFrequencyCount={(a) => getFrequencyCount(a, product)}
        getEstimatedVolume={(a) => getEstimatedVolume(a, product)}
        getUIConfig={() => getUIConfig(product)}
        disableAll={disableAll}
      />
    );
  }

  // Loading state
  if (
    isLoadingConfig ||
    isLoadingAutomations ||
    isLoadingProducts ||
    isLoadingCosts
  ) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-2">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">
            Loading capabilities...
          </p>
        </div>
      </div>
    );
  }

  return (
    <AutomationWrapper
      selectedPersonaId={selectedPersonaId}
      hasTwitterAuth={hasTwitterAuth}
    >
      {/* If no config -> show wizard on top */}
      {!generalConfig && (
        <InitialSetup
          isLoading={isSubmittingSetup}
          onSubmit={handleInitialSetup}
        />
      )}

      {/* If config exists -> show it (disable all buttons if `disableAll`) */}
      {generalConfig && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <GeneralConfigCard
                  config={generalConfig}
                  agentId={selectedPersonaId}
                />
              </div>
            </TooltipTrigger>
            {disableAll && (
              <TooltipContent>
                Complete setup or connect Twitter to enable changes.
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
      )}

      {/* ACTIVE Capabilities */}
      <div>
        <h2 className="text-xl font-semibold mb-2 text-white">
          Active Agent Capabilities
        </h2>
        {activeItems.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No capabilities are currently active.
          </p>
        ) : (
          <div className="grid grid-cols-2 gap-4">
            {activeItems.map(({ product, automation }) =>
              renderAutomationRow(automation, product),
            )}
          </div>
        )}

        {/* Cost breakdown */}
        {activeItems.length > 0 && (
          <div className="flex justify-center items-center pb-2 border-b mt-4">
            <div className="text-center text-md text-white">
              <span className="font-medium">
                {activeItems.length} active agent capabilities
              </span>
              <span className="mx-2">•</span>
              {(() => {
                const daily = totalDailyCost.toFixed(0);
                const monthly = (totalDailyCost * 30).toFixed(0);
                return (
                  <>
                    <span className="font-medium">
                      Estimated Credits -- {daily}
                    </span>
                    <span className="text-white/80">/day</span>
                    <span className="mx-2">•</span>
                    <span className="font-medium">{monthly}</span>
                    <span className="text-white/80">/month</span>
                  </>
                );
              })()}
            </div>
          </div>
        )}
      </div>

      {/* INACTIVE Capabilities (Catalog) */}
      <div>
        <h2 className="text-xl font-semibold mb-4 text-white">
          Agent Capabilities Catalog
        </h2>
        {catalogItems.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            All capabilities are active or no catalog items found.
          </p>
        ) : (
          <div className="grid grid-cols-4 gap-4">
            {catalogItems.map(({ product }) =>
              renderAutomationRow(null, product),
            )}
          </div>
        )}
      </div>
    </AutomationWrapper>
  );
}

/** Inactive row (no doc) */
function AutomationNoDocRow({
  product,
  handleCreateAutomation,
  disableAll,
}: {
  product: Product;
  handleCreateAutomation: (
    productSku: string,
    productName: string,
    productDescription: string,
  ) => Promise<void>;
  disableAll: boolean;
}) {
  const displayName = product.name;
  const costPerUse = product.credit_cost || 0;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className="
              border border-border
              rounded-lg p-2
              bg-[color:rgba(255,255,255,0.05)]
              hover:shadow-md
              transition-shadow
              overflow-hidden
              flex flex-col gap-2
            "
          >
            <div className="flex items-center gap-2">
              <div className="p-1 rounded-md">
                <Sparkles className="h-5 w-5 text-muted-foreground" />
              </div>
              <p className="font-semibold text-sm">{displayName}</p>
            </div>
            <p className="text-xs text-muted-foreground line-clamp-2">
              {product.description}
            </p>
            <div className="flex flex-col items-center justify-center gap-2 mt-2">
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="pointer-events-none"
                >
                  Inactive
                </Button>
                <Checkbox
                  checked={false}
                  disabled={disableAll}
                  onCheckedChange={async (checked) => {
                    if (checked) {
                      await handleCreateAutomation(
                        product.product_sku,
                        product.name,
                        product.description,
                      );
                    }
                  }}
                />
              </div>
              <div className="text-xs text-muted-foreground">
                Cost per use:{" "}
                <span className="font-semibold">{costPerUse}</span> credits
              </div>
            </div>
          </div>
        </TooltipTrigger>
        {disableAll && (
          <TooltipContent>
            Connect Twitter or complete setup to enable this feature.
          </TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  );
}

/** Active row (doc exists) */
function AutomationDocRow({
  product,
  automation,
  costs,
  handleConfigUpdate,
  handleToggleAutomation,
  getFrequencyCount,
  getEstimatedVolume,
  getUIConfig,
  disableAll,
}: {
  product: Product;
  automation: Automation;
  costs: AutomationCosts | undefined;
  handleConfigUpdate: (
    automationId: string,
    updates: Partial<Automation>,
  ) => void;
  handleToggleAutomation: (automationId: string, enabled: boolean) => void;
  getFrequencyCount: (a: Automation | null, p: Product) => number;
  getEstimatedVolume: (a: Automation | null, p: Product) => number;
  getUIConfig: () => ProductUIConfig;
  disableAll: boolean;
}) {
  const uiConfig = getUIConfig();
  const displayName = product.name;

  const isEnabled = automation.enabled;
  const freqCount = getFrequencyCount(automation, product);
  const docVolume = getEstimatedVolume(automation, product);
  const dailyCost =
    costs?.daily_costs[automation.automation_id]?.daily_cost || 0;
  const adjustedCost = dailyCost * freqCount;

  const [localFreqCount, setLocalFreqCount] = React.useState(freqCount);
  const [localVolume, setLocalVolume] = React.useState(docVolume);
  const [open, setOpen] = React.useState(false);

  const debouncedUpdateFreqCount = React.useMemo(
    () =>
      debounce((val: number) => {
        handleConfigUpdate(automation.automation_id, {
          metadata: {
            ...automation.metadata,
            frequency_count: val,
          },
        });
      }, 500),
    [automation, handleConfigUpdate],
  );
  const debouncedUpdateVolume = React.useMemo(
    () =>
      debounce((val: number) => {
        handleConfigUpdate(automation.automation_id, {
          metadata: {
            ...automation.metadata,
            estimated_daily_volume: val,
          },
        });
      }, 500),
    [automation, handleConfigUpdate],
  );

  React.useEffect(() => setLocalFreqCount(freqCount), [freqCount]);
  React.useEffect(() => setLocalVolume(docVolume), [docVolume]);

  React.useEffect(() => {
    return () => {
      debouncedUpdateFreqCount.cancel();
      debouncedUpdateVolume.cancel();
    };
  }, [debouncedUpdateFreqCount, debouncedUpdateVolume]);

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Collapsible
            open={open}
            onOpenChange={setOpen}
            className="
              border border-border
              rounded-lg p-2
              bg-[color:rgba(255,255,255,0.05)]
              hover:shadow-md
              transition-shadow
              overflow-hidden
            "
          >
            <CollapsibleTrigger
              className="
                flex items-center justify-between w-full cursor-pointer hover:no-underline
                min-h-[40px]
                [&_[data-state='closed']_svg]:hidden
                [&_[data-state='open']_svg]:hidden
                pb-2
                pt-2
              "
            >
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <div
                    className={cn(
                      "p-2 rounded-md",
                      isEnabled ? "bg-primary/20" : "bg-muted",
                    )}
                  >
                    <Sparkles
                      className={cn(
                        "h-5 w-5",
                        isEnabled ? "text-primary" : "text-muted-foreground",
                      )}
                    />
                  </div>
                  <p className="font-semibold text-sm">{displayName}</p>
                </div>
                <p className="text-xs text-muted-foreground line-clamp-2 text-left">
                  {product.description}
                </p>
              </div>

              {/* Right side */}
              <div className="flex flex-col items-end w-[170px]">
                {/* Row 1 */}
                <div className="flex items-center gap-3 justify-end w-full">
                  <Button
                    variant="outline"
                    size="sm"
                    className="pointer-events-none"
                  >
                    {isEnabled ? "Active" : "Inactive"}
                  </Button>
                  <Checkbox
                    checked={isEnabled}
                    disabled={disableAll}
                    onCheckedChange={(checked) =>
                      handleToggleAutomation(
                        automation.automation_id,
                        !!checked,
                      )
                    }
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                {/* Row 2: "Configuration" + custom arrow on the same line */}
                <div className="flex items-center gap-1 pt-6">
                  <span className="text-xs leading-none">Configuration</span>
                  <span
                    className={cn(
                      "font-bold text-xs leading-none",
                      open && "rotate-180",
                    )}
                  >
                    ▼
                  </span>
                </div>
              </div>
            </CollapsibleTrigger>

            {isEnabled && (
              <CollapsibleContent className="pt-1 space-y-4">
                <Card>
                  <CardContent className="space-y-1 pt-3">
                    {uiConfig.showActions && (
                      <div className="space-y-1 pt-2">
                        <Label>{uiConfig.actionLabel}</Label>
                        <div className="flex items-center gap-2 pt-0">
                          <Slider
                            min={1}
                            max={10}
                            step={1}
                            value={[localFreqCount]}
                            onValueChange={([val]) => {
                              setLocalFreqCount(val);
                              if (!disableAll) {
                                debouncedUpdateFreqCount(val);
                              }
                            }}
                            disabled={disableAll}
                            className="flex-1"
                          />
                          <div className="w-20 text-center font-mono">
                            {localFreqCount}
                          </div>
                        </div>
                      </div>
                    )}

                    {uiConfig.showVolume && (
                      <div className="space-y-1 pt-2">
                        <Label>{uiConfig.volumeLabel}</Label>
                        <div className="flex items-center gap-2 pt-1">
                          <Slider
                            min={1}
                            max={20}
                            step={1}
                            value={[localVolume]}
                            onValueChange={([val]) => {
                              setLocalVolume(val);
                              if (!disableAll) {
                                debouncedUpdateVolume(val);
                              }
                            }}
                            disabled={disableAll}
                            className="flex-1"
                          />
                          <div className="w-24 text-center font-mono">
                            {localVolume}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Cost Display */}
                    <div className="pt-2 pb-0">
                      <div className="flex justify-between items-center">
                        <div className="text-sm text-muted-foreground">
                          Estimated Cost
                        </div>
                        <div className="font-mono">
                          <span className="text-primary">
                            {adjustedCost.toFixed(0)}
                          </span>
                          <span className="text-sm text-muted-foreground ml-1">
                            credits/day
                          </span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </CollapsibleContent>
            )}
          </Collapsible>
        </TooltipTrigger>
        {disableAll && (
          <TooltipContent>
            Connect Twitter or complete setup to enable this feature.
          </TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  );
}

function AutomationRow({
  product,
  automation,
  costs,
  handleConfigUpdate,
  handleToggleAutomation,
  handleCreateAutomation,
  getFrequencyCount,
  getEstimatedVolume,
  getUIConfig,
  disableAll,
}: {
  product: Product;
  automation: Automation | null;
  costs: AutomationCosts | undefined;
  handleConfigUpdate: (
    automationId: string,
    updates: Partial<Automation>,
  ) => void;
  handleToggleAutomation: (automationId: string, enabled: boolean) => void;
  handleCreateAutomation: (
    productSku: string,
    productName: string,
    productDescription: string,
  ) => Promise<void>;
  getFrequencyCount: (a: Automation | null, p: Product) => number;
  getEstimatedVolume: (a: Automation | null, p: Product) => number;
  getUIConfig: () => ProductUIConfig;
  disableAll: boolean;
}) {
  if (!automation) {
    return (
      <AutomationNoDocRow
        product={product}
        handleCreateAutomation={handleCreateAutomation}
        disableAll={disableAll}
      />
    );
  }

  return (
    <AutomationDocRow
      product={product}
      automation={automation}
      costs={costs}
      handleConfigUpdate={handleConfigUpdate}
      handleToggleAutomation={handleToggleAutomation}
      getFrequencyCount={getFrequencyCount}
      getEstimatedVolume={getEstimatedVolume}
      getUIConfig={getUIConfig}
      disableAll={disableAll}
    />
  );
}
